import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { PlayCircle, TriangleAlert } from 'lucide-react';
import { api, parseApiError, type Competition, type Me, type Role } from '../lib/api';
import { canWrite } from '../lib/roles';
import { hasOrgManagerMembership } from '../lib/orgAccess';

// ─────────────────────────────────────────────────────────────────────────────
// ActivateCompetitionButton — the ONE Activate control for a PENDING
// competition. Extracted from TournamentSetup.tsx's anchor-row inline button
// (T2a, comp-setup guided wizard) so the cockpit's Status box and the new
// wizard's Review step share a single implementation instead of two parallel
// ones. Calls the SAME api.activateCompetition the inline control always
// called — no new endpoint, no new gate; this is a pure extraction.
//
// Founder/admin OR this comp's own org's ORG_ADMIN/ORG_OWNER (org-admin
// de-gate, widened 2026-07-24 as part of the single role-spine rewire — ADMIN
// can now run comps, same as ORG_OWNER; the old OWNER-ONLY carve-out from
// 2026-07-17 is retired). Mirrors the backend's canManageOrgCapability.
// `orgId`/`me` are optional so every existing call site (and this file's own
// tests) that passes only `role` keeps its EXACT prior behaviour — omitting
// them simply means the org-manager leg has nothing to check against, same
// as omitting orgId server-side. This is defence-in-depth UI gating — the
// backend does the real enforcement — but it means ANY caller that mounts
// this button for an unauthorized role safely gets nothing, without having
// to remember to gate it themselves.
//
// The "are you sure" step is a custom inline confirm (not window.confirm) so
// the copy can bold the specific things that lock, per Lisa's exact wording —
// a native confirm() can't carry that formatting. House rule followed: no
// colour/severity strip — the warning is carried by an icon + typography.
// ─────────────────────────────────────────────────────────────────────────────

export function ActivateCompetitionButton({
  compId,
  status,
  role,
  orgId,
  me,
  onActivated,
  className,
}: {
  compId: string;
  status: Competition['status'];
  role?: Role | null;
  /** The comp's OWN parent org (Competition.orgId) — for the org-owner leg. */
  orgId?: string | null;
  /** effectiveMe — for the org-owner membership check (view-as-aware). */
  me?: Me | null;
  onActivated?: () => void;
  className?: string;
}) {
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState('');

  const mut = useMutation({
    mutationFn: () => api.activateCompetition(compId),
    onSuccess: () => {
      setError('');
      setConfirming(false);
      onActivated?.();
    },
    onError: (e: Error) => setError(parseApiError(e).message),
  });

  // Founder/admin, or this comp's own org's ORG_ADMIN/ORG_OWNER — ORG_MEMBER
  // and below never see this control at all, regardless of what else they
  // can view on the surrounding page.
  if (!canWrite(role) && !hasOrgManagerMembership(me, orgId)) return null;
  // Nothing to activate once it's already LIVE/CLOSED.
  if (status !== 'PENDING') return null;

  if (confirming) {
    return (
      <div className={className}>
        <div className="rounded-lg bg-white/[0.04] p-3.5">
          <p className="flex items-center gap-1.5 font-display text-[11px] font-black uppercase tracking-wider text-ink-secondary">
            <TriangleAlert className="h-3.5 w-3.5" strokeWidth={2.25} /> Are you sure?
          </p>
          <p className="mt-2 text-xs leading-relaxed text-ink-body">
            Activating locks your <strong className="font-semibold text-ink-primary">team size</strong> permanently.
            Your <strong className="font-semibold text-ink-primary">dates and registration window</strong> were
            already fixed at creation. Your <strong className="font-semibold text-ink-primary">format and stages</strong> can
            still be edited until the first stage starts.
          </p>
          <div className="mt-3 flex gap-2">
            <button
              type="button"
              onClick={() => mut.mutate()}
              disabled={mut.isPending}
              className="btn-primary inline-flex items-center gap-1.5 px-3 py-1.5 text-xs"
            >
              <PlayCircle className="h-3.5 w-3.5" /> {mut.isPending ? 'Activating…' : 'Yes, activate'}
            </button>
            <button type="button" onClick={() => setConfirming(false)} className="btn-ghost px-3 py-1.5 text-xs">
              Cancel
            </button>
          </div>
          {error && <p className="mt-2 text-xs text-red-400">{error}</p>}
        </div>
      </div>
    );
  }

  return (
    <div className={className}>
      <button
        type="button"
        onClick={() => setConfirming(true)}
        disabled={mut.isPending}
        className="btn-primary inline-flex items-center gap-1.5 px-3 py-1.5 text-xs"
      >
        <PlayCircle className="h-3.5 w-3.5" />
        Activate
      </button>
      {error && <p className="mt-1 text-xs text-red-400">{error}</p>}
    </div>
  );
}
